import { useEffect, useState, useCallback } from "react";
import { Loader2, Armchair, X } from "lucide-react";
import api from "../api";
import { useSuccessFailure } from "../context/SuccessFailureContext";
import { Button, Input, Select, cn } from "./ui";

const STYLES = [
  { value: "round_tables", label: "Round Tables" },
  { value: "banquet", label: "Banquet (long tables)" },
  { value: "theater", label: "Theater (rows)" },
  { value: "classroom", label: "Classroom" },
  { value: "conference", label: "Conference / Boardroom" },
];

const SEAT_COLORS = {
  available: "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-200 hover:bg-green-200 dark:hover:bg-green-800",
  occupied: "bg-green-600 text-white",       // green = assigned
  reserved: "bg-yellow-400 text-yellow-900",
  blocked: "bg-red-500 text-white",
};

const SeatingManager = ({ event, canManage = true }) => {
  const { showFailureModal } = useSuccessFailure();
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState({ style: "round_tables", num_tables: 10, seats_per_table: 10, rows: 10, seats_per_row: 12, seats: 16 });
  const [activeSeat, setActiveSeat] = useState(null);
  const [assignName, setAssignName] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [attendees, setAttendees] = useState([]);

  const load = useCallback(async () => {
    if (!event?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/seating/events/${event.id}/`);
      setLayout(res.data.layout);
    } catch (e) {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [event?.id]);

  useEffect(() => { load(); }, [load]);

  // Load confirmed attendees so the organizer can seat real platform users.
  useEffect(() => {
    if (!canManage || !event?.id) return;
    api.get(`/seating/events/${event.id}/attendees/`)
      .then((res) => setAttendees(res.data || []))
      .catch(() => {});
  }, [canManage, event?.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/seating/events/${event.id}/generate/`, gen);
      setLayout(res.data.layout);
    } catch (e) {
      showFailureModal("Failed", e.response?.data?.detail || "Could not generate the layout.");
    } finally {
      setBusy(false);
    }
  };

  const openSeat = (seat) => {
    // Everyone can open a seat to see who's there; only managers get the assign controls.
    setActiveSeat(seat);
    setAssignName(seat.assigned_user_name || seat.assigned_name || "");
    setSelectedUserId("");
  };

  const assign = async () => {
    setBusy(true);
    try {
      const payload = selectedUserId ? { user_id: selectedUserId } : { name: assignName };
      await api.post(`/seating/seats/${activeSeat.id}/assign/`, payload);
      setActiveSeat(null);
      await load();
    } catch (e) {
      showFailureModal("Failed", "Could not assign the seat.");
    } finally { setBusy(false); }
  };

  const release = async () => {
    setBusy(true);
    try {
      await api.post(`/seating/seats/${activeSeat.id}/release/`, {});
      setActiveSeat(null);
      await load();
    } catch (e) {
      showFailureModal("Failed", "Could not release the seat.");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>;
  }

  // No layout yet — show generator (organizer) or empty state (attendee)
  if (!layout) {
    if (!canManage) {
      return <p className="text-gray-500 dark:text-gray-400 py-8 text-center">No seating plan has been published yet.</p>;
    }
    return (
      <div className="space-y-4 max-w-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create a floor plan</h2>
        <Select label="Style" value={gen.style} onChange={(e) => setGen({ ...gen, style: e.target.value })}>
          {STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        {(gen.style === "round_tables" || gen.style === "banquet") && (
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" min="1" label="Number of tables" value={gen.num_tables}
              onChange={(e) => setGen({ ...gen, num_tables: e.target.value })} />
            <Input type="number" min="1" label="Seats per table" value={gen.seats_per_table}
              onChange={(e) => setGen({ ...gen, seats_per_table: e.target.value })} />
          </div>
        )}
        {(gen.style === "theater" || gen.style === "classroom") && (
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" min="1" label="Rows" value={gen.rows}
              onChange={(e) => setGen({ ...gen, rows: e.target.value })} />
            <Input type="number" min="1" label="Seats per row" value={gen.seats_per_row}
              onChange={(e) => setGen({ ...gen, seats_per_row: e.target.value })} />
          </div>
        )}
        {gen.style === "conference" && (
          <Input type="number" min="1" label="Total seats" value={gen.seats}
            onChange={(e) => setGen({ ...gen, seats: e.target.value })} />
        )}
        <Button onClick={generate} loading={busy} leftIcon={<Armchair size={16} />}>Generate floor plan</Button>
      </div>
    );
  }

  const stats = layout.stats || {};

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{layout.name}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {layout.style_display} · {stats.taken}/{stats.total_seats} seats taken
          </p>
        </div>
        {canManage && (
          <Button size="sm" variant="outline" onClick={() => setLayout(null)}>Regenerate</Button>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-200 dark:bg-gray-600" /> Available</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-600" /> Assigned</span>
      </div>

      {/* Map */}
      {(() => {
        const isRound = (layout.style_display || "").toLowerCase().includes("round");
        const seatBtn = (seat) => (
          <button
            key={seat.id}
            onClick={() => openSeat(seat)}
            title={seat.assigned_user_name || seat.label}
            className={cn("w-9 h-9 rounded-full text-[10px] font-semibold flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer",
              SEAT_COLORS[seat.status] || SEAT_COLORS.available)}
          >
            {seat.number}
          </button>
        );
        return (
          <div className={cn("grid gap-5",
            layout.tables[0]?.kind === "row" ? "grid-cols-1" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4")}>
            {layout.tables.map((t) => (
              <div key={t.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 text-center">{t.name}</p>
                {isRound && t.kind !== "row" ? (
                  // Seats laid out in a ring around a round table.
                  <div className="relative mx-auto" style={{ width: 168, height: 168 }}>
                    <div className="absolute rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600"
                         style={{ left: 44, top: 44, width: 80, height: 80 }} />
                    {t.seats.map((seat, i) => {
                      const n = t.seats.length || 1;
                      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
                      const R = 66;
                      const x = 84 + Math.cos(angle) * R - 18;
                      const y = 84 + Math.sin(angle) * R - 18;
                      return (
                        <div key={seat.id} className="absolute" style={{ left: x, top: y }}>{seatBtn(seat)}</div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={cn("flex flex-wrap gap-2 justify-center", t.kind === "row" && "flex-nowrap overflow-x-auto justify-start")}>
                    {t.seats.map((seat) => seatBtn(seat))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Seat details + assign modal */}
      {activeSeat && (() => {
        const occupied = activeSeat.status === "occupied";
        const occupant = activeSeat.assigned_user_name || activeSeat.assigned_name;
        const statusLabel = occupied ? "Occupied"
          : activeSeat.status === "reserved" ? "Reserved"
          : activeSeat.status === "blocked" ? "Blocked" : "Available";
        return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
             onClick={() => setActiveSeat(null)}>
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Seat {activeSeat.label}</h3>
              <button onClick={() => setActiveSeat(null)}><X size={20} className="text-gray-400" /></button>
            </div>

            {/* Who's here — shown to everyone */}
            <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-400">{statusLabel}</p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {occupied ? (occupant || "Assigned") : "No one assigned yet"}
              </p>
            </div>

            {canManage ? (
              <>
                {attendees.length > 0 && (
                  <Select label="Pick a confirmed attendee" value={selectedUserId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedUserId(id);
                      const a = attendees.find((x) => String(x.id) === String(id));
                      if (a) setAssignName(a.name);
                    }}>
                    <option value="">— Type a name below instead —</option>
                    {attendees.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.email})</option>)}
                  </Select>
                )}
                <Input label="…or guest name" value={assignName} disabled={!!selectedUserId}
                  onChange={(e) => setAssignName(e.target.value)} placeholder="e.g. Jane Doe" />
                <div className="flex gap-2">
                  {occupied && (
                    <Button variant="danger" fullWidth loading={busy} onClick={release}>Release</Button>
                  )}
                  <Button fullWidth loading={busy} onClick={assign} disabled={!assignName.trim()}>
                    {occupied ? "Reassign" : "Assign"}
                  </Button>
                </div>
              </>
            ) : (
              <Button fullWidth variant="outline" onClick={() => setActiveSeat(null)}>Close</Button>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default SeatingManager;
